import { apiService } from './apiService';

export type GlSettings = {
  gl_enabled: boolean;
  account_count: number;
};

export type GlAccount = {
  id: number;
  code: string;
  name: string;
  account_type: string;
  is_system: boolean;
  is_active: boolean;
};

export type GlJournalLine = {
  id: number;
  account_id: number;
  account_code?: string | null;
  account_name?: string | null;
  debit: number;
  credit: number;
  location_id?: number | null;
  memo?: string | null;
  gst_category?: string | null;
};

export type GlJournal = {
  id: number;
  entry_number: string;
  entry_date: string;
  fiscal_year: number;
  fiscal_month: number;
  source_type: string;
  source_id: number;
  location_id?: number | null;
  status: string;
  memo?: string | null;
  reversal_of_id?: number | null;
  total_debit: number;
  total_credit: number;
  lines: GlJournalLine[];
};

export type TrialBalanceRow = {
  account_id: number;
  code: string;
  name: string;
  account_type: string;
  debit: number;
  credit: number;
};

export type TrialBalanceReport = {
  start_date: string;
  end_date: string;
  rows: TrialBalanceRow[];
  total_debit: number;
  total_credit: number;
};

export type ProfitLossReport = {
  start_date: string;
  end_date: string;
  revenue: number;
  expenses: number;
  cogs: number;
  gross_profit: number;
  net_profit: number;
  revenue_rows: TrialBalanceRow[];
  expense_rows: TrialBalanceRow[];
};

export async function getGlSettings(): Promise<GlSettings> {
  return apiService.get<GlSettings>('/gl/settings');
}

export async function updateGlSettings(glEnabled: boolean): Promise<GlSettings> {
  return apiService.put<GlSettings>('/gl/settings', { gl_enabled: glEnabled });
}

export async function listGlAccounts(): Promise<GlAccount[]> {
  return apiService.get<GlAccount[]>('/gl/accounts');
}

export async function listGlJournals(startDate: string, endDate: string): Promise<GlJournal[]> {
  return apiService.get<GlJournal[]>('/gl/journals', {
    start_date: startDate,
    end_date: endDate,
  });
}

export async function getGlJournal(id: number): Promise<GlJournal> {
  return apiService.get<GlJournal>(`/gl/journals/${id}`);
}

export async function getTrialBalance(startDate: string, endDate: string): Promise<TrialBalanceReport> {
  return apiService.get<TrialBalanceReport>('/gl/reports/trial-balance', {
    start_date: startDate,
    end_date: endDate,
  });
}

export async function getProfitLoss(startDate: string, endDate: string): Promise<ProfitLossReport> {
  return apiService.get<ProfitLossReport>('/gl/reports/profit-loss', {
    start_date: startDate,
    end_date: endDate,
  });
}

export async function repostGlSource(sourceType: string, sourceId: number): Promise<GlJournal> {
  return apiService.post<GlJournal>(`/gl/repost/${sourceType}/${sourceId}`, {});
}
